import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ConferenceStatus } from '@yandex-360/telemost-sdk';
import { Slot } from '@yandex-video-platform/goloom-sdk';

import { AccessLevel, BroadcastData, RoomConferenceState } from '../../../../types';
import {
    CancelCallActionParams,
    ConferenceConfig,
    ConferenceGuestConfig,
    DisconnectPayload,
    JoinConferencePayload,
    ParticipantID,
    ParticipantsById,
    ParticipantState,
    ReconnectPayload,
    SlotState,
    UserParams,
} from '../../types';
import { RemoveParticipantPayload } from './types';

type Slots = { prev: Slot[]; current: Slot[]; next: Slot[]; maxOffset: number };

type ConferenceState = {
    participants: ParticipantID[];
    participantsByIds: ParticipantsById;
    conferenceStatus: ConferenceStatus;
    config?: ConferenceConfig;
    joinedAsGuest: boolean;
    myOldUserIds: ParticipantID[];
    myUserId?: ParticipantID;
    settings?: RoomConferenceState;
    showScreenSharingPermissionsHelp?: boolean;
    stateVersion: number;
    closeRoomInitiatedBySelf: boolean;
    slots: { prev: SlotState[]; current: SlotState[]; next: SlotState[]; maxOffset: number };
    slotsReceived: boolean;
};

const initialState: ConferenceState = {
    stateVersion: -1,
    participants: [],
    participantsByIds: {},
    conferenceStatus: ConferenceStatus.DISCONNECTED,
    joinedAsGuest: true,
    myOldUserIds: [],
    closeRoomInitiatedBySelf: false,
    slots: { prev: [], current: [], next: [], maxOffset: 0 },
    slotsReceived: false,
};

const conferenceSlice = createSlice({
    name: 'conference',
    initialState,
    reducers: {
        setConferenceStatus(state, action: PayloadAction<ConferenceStatus>) {
            state.conferenceStatus = action.payload;
        },

        setParticipants(state, action: PayloadAction<ConferenceState['participants']>) {
            state.participants = action.payload;
        },
        setParticipantsByIds(state, action: PayloadAction<ConferenceState['participantsByIds']>) {
            state.participantsByIds = action.payload;
        },
        setParticipantById(state, action: PayloadAction<ParticipantState>) {
            state.participantsByIds[action.payload.id] = action.payload;
        },
        removeParticipant(state, action: PayloadAction<ParticipantID>) {
            state.participants = state.participants.filter((id) => id !== action.payload);
        },
        removeParticipantById(state, action: PayloadAction<ParticipantID>) {
            const participantId = action.payload;
            const participant = state.participantsByIds[participantId];
            if (participant) {
                delete state.participantsByIds[participantId];
            }
        },
        setJoinedAsGuest(state: ConferenceState, action: PayloadAction<boolean>) {
            state.joinedAsGuest = action.payload;
        },
        setMyUserId(state: ConferenceState, action: PayloadAction<ParticipantID>) {
            if (state.myUserId) {
                state.myOldUserIds.push(state.myUserId);
            }
            state.myUserId = action.payload;
        },
        setRoomConferenceConfig(state, action: PayloadAction<ConferenceConfig | undefined>) {
            const config = action.payload;

            if (!config) {
                state.config = config;
            } else {
                const { conference_state: conferenceState, ...roomSettings } = config;
                state.config = roomSettings;

                if (conferenceState) {
                    state.settings = conferenceState;
                }
            }
        },
        initBroadcastSettings(state: ConferenceState, action: PayloadAction<BroadcastData>) {
            if (!state.settings) {
                return state;
            }
            state.settings.broadcast_data = { ...action.payload };
        },

        updateBroadcastSettings(state: ConferenceState, action: PayloadAction<Partial<BroadcastData>>) {
            if (!state.settings?.broadcast_data) {
                return state;
            }
            state.settings.broadcast_data = {
                ...state.settings.broadcast_data,
                ...action.payload,
            };
        },

        updateConferenceAccessLevel(state: ConferenceState, action: PayloadAction<AccessLevel>) {
            if (!state.settings) {
                state.settings = {} as RoomConferenceState;
            }
            state.settings.access_level = action.payload;
        },
        setShowScreenSharingPermissionsHelp(state: ConferenceState, action: PayloadAction<boolean>) {
            state.showScreenSharingPermissionsHelp = action.payload;
        },
        setConferenceStateVersion(state: ConferenceState, action: PayloadAction<number>) {
            state.stateVersion = action.payload;
        },
        setCloseRoomInitiatedBySelf(state: ConferenceState, action: PayloadAction<boolean>) {
            state.closeRoomInitiatedBySelf = action.payload;
        },
        resetConferenceState(state: ConferenceState) {
            return {
                ...initialState,
                // resetCall can happen before the SDK reports ROOM_HAS_BEEN_CLOSED.
                closeRoomInitiatedBySelf: state.closeRoomInitiatedBySelf,
            };
        },
        updateSlots(state: ConferenceState, action: PayloadAction<Slots>) {
            state.slots = action.payload;
            if (action.payload.current.length > 0) {
                state.slotsReceived = true;
            }
        },
    },
});

export const {
    setConferenceStatus,
    setParticipants,
    setParticipantsByIds,
    setParticipantById,
    setJoinedAsGuest,
    setConferenceStateVersion,
    setMyUserId,
    setRoomConferenceConfig,
    initBroadcastSettings,
    updateBroadcastSettings,
    updateConferenceAccessLevel,
    setShowScreenSharingPermissionsHelp,
    removeParticipantById,
    removeParticipant,
    resetConferenceState,
    setCloseRoomInitiatedBySelf,
    updateSlots,
} = conferenceSlice.actions;
export const conferenceReducer = conferenceSlice.reducer;
export const updateParticipants = createAction<ConferenceState['participantsByIds']>('conference/updateParticipants');
export const updateParticipant = createAction<ParticipantState>('conference/updateParticipant');
export const startAsGuestConferenceAction = createAction<ConferenceGuestConfig>('conference/startAsGuest');
export const removeParticipantAction = createAction<RemoveParticipantPayload>('conference/removeParticipantAction');
export const participantsRemovedAction = createAction<number>('conference/participantsRemoved');
export const participantsAddedAction = createAction<number>('conference/participantsAdded');
export const participantTurnedVideoOn = createAction<string>('conference/participantTurnedVideoOn');
export const participantTurnedVideoOnSuccess = createAction<string>('conference/participantTurnedVideoOnSuccess');
export const participantTurnedVideoOff = createAction<string>('conference/participantTurnedVideoOff');
export const participantScreenSharingSuccess = createAction('conference/participantScreenSharingSuccess');
export const connectedToConferenceAction = createAction('conference/connected');
export const connectedToConferenceDoneAction = createAction('conference/connectedDone');
export const disconnectedFromConferenceAction = createAction('conference/disconnected');
export const disconnectedFromConferenceDoneAction =
    createAction<Maybe<DisconnectPayload>>('conference/disconnectedDone');
export const disconnectConferenceAction = createAction<DisconnectPayload>('conference/disconnect');
export const reconnectConference = createAction<Maybe<ReconnectPayload>>('conference/reconnect');
export const userJoinAction = createAction<UserParams>('conference/userJoin');
export const userLeftAction = createAction<UserParams>('conference/userLeft');
export const joinConference = createAction<JoinConferencePayload>('conference/join');
export const cancelCallAction = createAction<CancelCallActionParams | undefined>('conference/cancel');
export const fetchMe = createAction('conference/fetchMe');
export const forceRequestStates = createAction('conference/requestStates');
